import { useQuery } from "@tanstack/react-query";
import { storage } from "@/utils/storage";
import { api } from "@/lib/axios";

export const operatorBase = "/organizations/operator-console";
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  capabilities?: string[];
  configuration_management?: "CUSTOMER_MANAGED" | "INTERNAL_MANAGED";
}
export interface OperatorContext {
  platform_role: "PLATFORM_ADMIN" | "TENANT_OPERATOR" | null;
  tenants: Tenant[];
}
export interface TenantDetail {
  tenant: Tenant;
  capabilities: string[];
  members: {
    id: string;
    user_id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    is_active: boolean;
    web_access: boolean;
    can_manage: boolean;
    protected_reason: string | null;
    projects: { id: number; name: string; role: string }[];
  }[];
  projects: { id: number; name: string }[];
  forms: { id: number; name: string; project_id: number }[];
  events: {
    id: string;
    event_type: string;
    summary: string;
    created_at: string;
  }[];
}
export interface Assignment {
  id: string;
  email: string;
  organization_id: string;
  organization_name: string;
  access: string;
  is_active: boolean;
  expires_at: string | null;
}
export interface AccessState {
  allowed: boolean;
  reasons: string[];
}
export interface ProjectAccess extends AccessState {
  id: number;
  name: string;
  role: string | null;
  project_owner: boolean;
  resource_scope_restricted: boolean;
}
export interface MemberDetail {
  membership: {
    id: string;
    user_id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    is_active: boolean;
    current: boolean;
    web_access: boolean;
    access_kind: string;
    access_expires_at: string | null;
  };
  protected_reason: string | null;
  effective_access: {
    tenant: AccessState;
    web: AccessState;
    projects: ProjectAccess[];
  };
  events: {
    id: string;
    event_type: string;
    summary: string;
    created_at: string;
  }[];
  handoffs: {
    id: string;
    event_type: string;
    summary: string;
    created_at: string;
  }[];
}
export interface Assignments {
  assignments: Assignment[];
  tenants: Tenant[];
}
function operatorIdentity() {
  try {
    const user = JSON.parse(storage.getItem("user_info") || "{}");
    return String(user.id || user.email || "session");
  } catch {
    return "session";
  }
}
export const operatorKeys = {
  get context() {
    return ["tenant-operations", operatorIdentity(), "context"] as const;
  },
  tenant: (id: string) =>
    ["tenant-operations", operatorIdentity(), "tenant", id] as const,
  member: (tenantId: string, memberId: string) =>
    ["tenant-operations", operatorIdentity(), "tenant", tenantId, "member", memberId] as const,
  schema: (tenantId: string, formId: number) =>
    ["tenant-operations", operatorIdentity(), "tenant", tenantId, "schema", formId] as const,
  captures: (tenantId: string) =>
    ["tenant-operations", operatorIdentity(), "tenant", tenantId, "captures"] as const,
  capture: (tenantId: string, id: number) =>
    ["tenant-operations", operatorIdentity(), "tenant", tenantId, "capture", id] as const,
  get assignments() {
    return ["tenant-operations", operatorIdentity(), "assignments"] as const;
  },
};
export function useMemberDetail(tenantId: string, memberId: string | null) {
  return useQuery({
    enabled: !!memberId,
    queryKey: operatorKeys.member(tenantId, memberId || ""),
    queryFn: async () =>
      (
        await api.get<MemberDetail>(
          `${operatorBase}/tenants/${tenantId}/members/${memberId}/detail/`
        )
      ).data,
    retry: false,
  });
}
export function useOperatorContext(enabled = true) {
  return useQuery({
    enabled,
    queryKey: operatorKeys.context,
    queryFn: async () =>
      (await api.get<OperatorContext>(`${operatorBase}/context/`)).data,
    retry: false,
    staleTime: 60_000,
  });
}

export const operatorExportsApi = {
  listExports: (orgId: string) =>
    api
      .get<import("@/features/customer-operations/api").TenantExport[]>(
        `${operatorBase}/tenants/${orgId}/exports/`
      )
      .then((r) => r.data),
  requestExport: (
    orgId: string,
    key: string,
    payload: {
      scope: "all" | "projects";
      project_ids: number[];
      include_files?: boolean;
      reason?: string;
    }
  ) =>
    api
      .post<import("@/features/customer-operations/api").TenantExport>(
        `${operatorBase}/tenants/${orgId}/exports/`,
        payload,
        { headers: { "Idempotency-Key": key } }
      )
      .then((r) => r.data),
  downloadExport: (orgId: string, id: string) =>
    api
      .get<{ download_url: string; expires_in: number }>(`${operatorBase}/tenants/${orgId}/exports/${id}/`, {
        params: { download: 1, direct: 1 },
      })
      .then((r) => r.data),
};

export interface SchemaField {
  id?: number;
  name: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
  help_text?: string;
  placeholder?: string;
  order: number;
  validation?: Record<string, unknown>;
}
export interface FormSchema {
  id: number;
  name: string;
  project_id: number;
  fields: SchemaField[];
  layout: unknown;
  current_version_number: number | null;
  schema_status: {
    synced: boolean;
    unversioned_changes: boolean;
    submissions_on_older_versions: number;
  };
  capture_types: string[];
  updated_at: string;
}
export function useFormSchema(tenantId: string, formId: number | null) {
  return useQuery({
    enabled: !!formId,
    queryKey: operatorKeys.schema(tenantId, formId || 0),
    queryFn: async () =>
      (
        await api.get<FormSchema>(
          `${operatorBase}/tenants/${tenantId}/forms/${formId}/schema/`
        )
      ).data,
    retry: false,
  });
}
export function saveFormSchema(
  tenantId: string,
  formId: number,
  payload: {
    fields: SchemaField[];
    reason: string;
    expected_updated_at: string;
  }
) {
  return api
    .put<FormSchema>(
      `${operatorBase}/tenants/${tenantId}/forms/${formId}/schema/`,
      payload
    )
    .then((r) => r.data);
}

export interface Capture {
  id: number;
  client_id: string | null;
  status: string;
  form_id: number;
  form_name: string;
  project_id: number;
  submitted_at: string | null;
  created_at: string;
  form_schema_version: number | null;
  submitted_by: string | null;
  expected_count: number;
  linked_count: number;
  missing_media: string[];
  failed_jobs: number;
  next_action: string;
}
export interface CaptureDiagnostics {
  receipt: {
    id: number;
    status: string;
    form_name: string;
    project_id: number;
    submitted_at: string | null;
    created_at: string;
    updated_at: string;
    form_schema_version: number | null;
    current_version_number: number | null;
    submitted_by: string | null;
  };
  expected_media: { field: string; type: string; cardinality: string }[];
  linked_media: { id: number; kind: string; source: string; captured_at: string | null }[];
  missing_media: string[];
  processing: { field: string; status: string; error: string; completed_at: string | null }[];
  next_action: string;
}
export function useCaptures(tenantId: string) {
  return useQuery({
    queryKey: operatorKeys.captures(tenantId),
    queryFn: async () =>
      (await api.get<{ captures: Capture[] }>(`${operatorBase}/tenants/${tenantId}/captures/`)).data,
    retry: false,
  });
}
export function useCaptureDiagnostics(tenantId: string, captureId: number | null) {
  return useQuery({
    enabled: !!captureId,
    queryKey: operatorKeys.capture(tenantId, captureId || 0),
    queryFn: async () =>
      (
        await api.get<CaptureDiagnostics>(
          `${operatorBase}/tenants/${tenantId}/captures/${captureId}/`
        )
      ).data,
    retry: false,
  });
}

export interface SLAConfig {
  id: string;
  ticket_type_id: string;
  ticket_type_name: string;
  state: string;
  threshold_minutes: number;
  workflow: Record<string, unknown>;
  is_active: boolean;
}
export interface TicketTypeConfig {
  id: string;
  name: string;
  project_id: number;
  state_machine: {
    initial: string;
    states: string[];
    outcomes?: Record<string, string>;
    transitions: { from: string; to: string; roles?: string[] }[];
    close_detail_on_states?: string[];
    [key: string]: unknown;
  };
  communication_config: Record<string, unknown>;
  updated_at: string;
  history?: {
    id: number;
    created_at: string;
    actor: string;
    before?: unknown;
    after?: unknown;
    reason?: string;
  }[];
}
export function useSLAConfig(tenantId: string) {
  return useQuery({
    queryKey: ["tenant-operations", operatorIdentity(), "tenant", tenantId, "sla"],
    queryFn: async () =>
      (await api.get<{ policies: SLAConfig[] }>(`${operatorBase}/tenants/${tenantId}/sla-policies/`)).data,
    retry: false,
  });
}
export function saveSLAConfig(
  tenantId: string,
  policyId: string,
  payload: { threshold_minutes: number; inactivity_reminders: Record<string, unknown>; reason: string }
) {
  return api
    .put<SLAConfig>(`${operatorBase}/tenants/${tenantId}/sla-policies/${policyId}/`, payload)
    .then((r) => r.data);
}
export function previewConfiguration(
  tenantId: string,
  payload: Record<string, unknown>
) {
  return api
    .post<{ kind: string; impact: Record<string, unknown> }>(
      `${operatorBase}/tenants/${tenantId}/configuration-preview/`,
      payload
    )
    .then((r) => r.data);
}
export function useTicketTypeConfig(tenantId: string, ticketTypeId: string | null) {
  return useQuery({
    enabled: !!ticketTypeId,
    queryKey: ["tenant-operations", operatorIdentity(), "tenant", tenantId, "ticket-type", ticketTypeId],
    queryFn: async () =>
      (
        await api.get<TicketTypeConfig>(
          `${operatorBase}/tenants/${tenantId}/ticket-types/${ticketTypeId}/configuration/`
        )
      ).data,
    retry: false,
  });
}
export function saveTicketTypeConfig(
  tenantId: string,
  ticketTypeId: string,
  payload: { state_machine: unknown; reason: string; expected_updated_at?: string }
) {
  return api
    .put<TicketTypeConfig>(
      `${operatorBase}/tenants/${tenantId}/ticket-types/${ticketTypeId}/configuration/`,
      payload
    )
    .then((r) => r.data);
}

export interface ExportCompleteness {
  export_id: string;
  status: string;
  downloadable: boolean;
  artifact_size: number | null;
  artifact_sha256: string;
  record_counts: Record<string, number>;
  total_records: number;
  file_count: number;
  archived_files: number;
  expected_media_fields: number;
  missing_media: string[];
  sheets: string[];
  checks: { code: string; ok: boolean }[];
  next_action: string;
}
export interface ReportRecipe {
  id: string;
  name: string;
  project_id: number;
  project_name: string;
  is_published: boolean;
  roles: string[];
}
export function useReports(tenantId: string) {
  return useQuery({
    queryKey: ["tenant-operations", operatorIdentity(), "tenant", tenantId, "reports"],
    queryFn: async () =>
      (await api.get<{ reports: ReportRecipe[] }>(`${operatorBase}/tenants/${tenantId}/reports/`)).data,
    retry: false,
  });
}
export function useExportCompleteness(tenantId: string, exportId: string | null) {
  return useQuery({
    enabled: !!exportId,
    queryKey: ["tenant-operations", operatorIdentity(), "tenant", tenantId, "completeness", exportId],
    queryFn: async () =>
      (
        await api.get<ExportCompleteness>(
          `${operatorBase}/tenants/${tenantId}/exports/${exportId}/completeness/`
        )
      ).data,
    retry: false,
  });
}

export type ResourceMode = "all" | "none" | "selected";
export interface ResourceItem {
  id: number;
  name: string;
  can_view: boolean;
  can_edit: boolean;
}
export interface ResourceBlock {
  mode: ResourceMode;
  can_edit_ceiling: boolean;
  items: ResourceItem[];
}
export interface MemberResources {
  project: { id: number; name: string };
  role: string;
  unrestricted: boolean;
  layers: ResourceBlock;
  forms: ResourceBlock;
}
export function memberResourcesKey(
  membershipId: string | null,
  projectId: number | null
) {
  return [
    "tenant-operations",
    operatorIdentity(),
    "member-resources",
    membershipId,
    projectId,
  ] as const;
}
export function useMemberResources(
  tenantId: string,
  membershipId: string | null,
  projectId: number | null
) {
  return useQuery({
    enabled: !!membershipId && !!projectId,
    queryKey: memberResourcesKey(membershipId, projectId),
    queryFn: async () =>
      (
        await api.get<MemberResources>(
          `${operatorBase}/tenants/${tenantId}/members/${membershipId}/resources/`,
          { params: { project: projectId } }
        )
      ).data,
    retry: false,
  });
}
export interface ResourceBlockPayload {
  mode: ResourceMode;
  can_edit?: boolean;
  ids?: number[];
  overrides?: { resource_id: number; can_view?: boolean; can_edit?: boolean }[];
}
export function saveMemberResources(
  tenantId: string,
  membershipId: string,
  payload: {
    project_id: number;
    layers: ResourceBlockPayload;
    forms: ResourceBlockPayload;
    reason: string;
  }
) {
  return api
    .put<MemberResources>(
      `${operatorBase}/tenants/${tenantId}/members/${membershipId}/resources/`,
      payload
    )
    .then((r) => r.data);
}
